import { SettingsData } from "../types/types";
import { useState, useEffect } from 'react';

interface CalendarProps {
  selectedDate: string;
  handleDateChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  formatDate: (date: string) => string;
  isDayOpen: boolean;
  settings: SettingsData;
  getDayOfWeek: (date: string) => string;
  type: 'vr' | 'gokart' | 'simulator' | 'playhouse';
}

const Calendar = ({
  selectedDate,
  handleDateChange,
  formatDate,
  isDayOpen,
  settings,
  getDayOfWeek,
  type
}: CalendarProps) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendar, setCalendar] = useState<Date[][]>([]);

  const generateCalendar = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const firstMonday = new Date(firstDay);
    while (firstMonday.getDay() !== 1) {
      firstMonday.setDate(firstMonday.getDate() - 1);
    }

    const weeks: Date[][] = [];
    let currentWeek: Date[] = [];
    const current = new Date(firstMonday);

    while (
      current <= lastDay ||
      current.getDay() !== 1 ||
      weeks.length < 6
    ) {
      if (current.getDay() === 1 && currentWeek.length > 0) {
        weeks.push(currentWeek);
        currentWeek = [];
      }

      currentWeek.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    if (currentWeek.length > 0) {
      weeks.push(currentWeek);
    }

    setCalendar(weeks);
  };

  useEffect(() => {
    generateCalendar(currentMonth);
  }, [currentMonth]);

  const changeMonth = (increment: number) => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(newDate.getMonth() + increment);
    setCurrentMonth(newDate);
  };

  const dayNames = ['H', 'K', 'Sze', 'Cs', 'P', 'Szo', 'V'];

  const isToday = (date: Date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
  };

  const isSelected = (date: Date) => {
    const selected = new Date(selectedDate);
    return date.getDate() === selected.getDate() &&
      date.getMonth() === selected.getMonth() &&
      date.getFullYear() === selected.getFullYear();
  };

  const isCurrentMonth = (date: Date) => {
    return date.getMonth() === currentMonth.getMonth();
  };

  const formatDateToString = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Új függvény a zártkörű események ellenőrzéséhez
  const hasPrivateEvent = (date: Date) => {
    const dateString = formatDateToString(date);
    return settings.privateBlocks.some(block => {
      // Check if block.date is a valid date string
      if (!block.date) return false;
      
      try {
        const blockDate = new Date(block.date);
        // Check if blockDate is valid before using toISOString
        if (isNaN(blockDate.getTime())) return false;
        
        return blockDate.toISOString().split('T')[0] === dateString &&
          block.serviceType === type;
      } catch (error) {
        console.error("Invalid date in privateBlocks:", block.date);
        return false;
      }
    });
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Hónap választó */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4">
          <div className="flex items-center justify-between text-white">
            <button
              onClick={() => changeMonth(-1)}
              className="p-2 hover:bg-blue-500 rounded-full transition-colors duration-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h2 className="text-xl font-semibold">
              {currentMonth.toLocaleString('hu-HU', { year: 'numeric', month: 'long' })}
            </h2>
            <button
              onClick={() => changeMonth(1)}
              className="p-2 hover:bg-blue-500 rounded-full transition-colors duration-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Naptár */}
        <div className="p-4">
          {/* Napok fejléc */}
          <div className="grid grid-cols-7 mb-2">
            {dayNames.map((day) => (
              <div
                key={day}
                className="text-center text-sm font-semibold text-gray-600 py-2"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Napok rács - módosított verzió */}
          <div className="grid grid-cols-7 gap-1">
            {calendar.map((week, weekIndex) =>
              week.map((date, dateIndex) => {
                // Módosított isDisabled ellenőrzés
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const checkDate = new Date(date);
                checkDate.setHours(0, 0, 0, 0);
                const isDisabled = checkDate < today;

                const dateString = formatDateToString(date);
                const hasPrivate = hasPrivateEvent(date);

                return (
                  <button
                    key={`${weekIndex}-${dateIndex}`}
                    onClick={() => {
                      if (!isDisabled) {
                        const event = {
                          target: {
                            value: dateString,
                            type: 'date'
                          }
                        } as React.ChangeEvent<HTMLInputElement>;
                        handleDateChange(event);
                      }
                    }}
                    disabled={isDisabled}
                    className={`
                      aspect-square p-2 rounded-lg relative
                      flex items-center justify-center
                      text-sm font-medium transition-all duration-200
                      ${!isCurrentMonth(date) ? 'text-gray-400' : 'text-gray-900'}
                      ${isDisabled
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'hover:bg-blue-50 hover:text-blue-600'
                      }
                      ${isToday(date) ? 'ring-2 ring-blue-600 ring-offset-2' : ''}
                      ${isSelected(date) ? 'bg-blue-600 text-white hover:bg-blue-700' : ''}
                    `}
                  >
                    <span className="relative z-10">{date.getDate()}</span>
                    {hasPrivate && (
                      <span
                        className="absolute top-1 right-1 w-2 h-2 bg-yellow-400 rounded-full z-20"
                        title="Zártkörű esemény ezen a napon"
                      ></span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Nyitvatartási információ - módosított verzió */}
        <div className="p-4 border-t border-gray-200">
          <p className="text-sm text-gray-600 mb-2">
            {formatDate(selectedDate)}
          </p>
          <div className="space-y-2">
            {isDayOpen ? (
              <div className="flex items-center text-green-600 bg-green-50 p-3 rounded-lg">
                <div className="w-2 h-2 rounded-full bg-green-600 mr-2"></div>
                <p className="text-sm font-medium">
                  Nyitva: {settings.schedule[getDayOfWeek(selectedDate) as keyof typeof settings.schedule].openingHour}:00 -
                  {settings.schedule[getDayOfWeek(selectedDate) as keyof typeof settings.schedule].closingHour}:00
                </p>
              </div>
            ) : (
              <div className="flex items-center text-red-600 bg-red-50 p-3 rounded-lg">
                <div className="w-2 h-2 rounded-full bg-red-600 mr-2"></div>
                <p className="text-sm font-medium">
                  Ezen a napon zárva vagyunk
                </p>
              </div>
            )}

            {/* Zártkörű események megjelenítése a kiválasztott napra */}
            {hasPrivateEvent(new Date(selectedDate)) && (
              <div className="flex items-center text-yellow-700 bg-yellow-50 p-3 rounded-lg">
                <div className="w-2 h-2 rounded-full bg-yellow-400 mr-2"></div>
                <div className="text-sm">
                  <p className="font-medium mb-1">Zártkörű események ezen a napon:</p>
                  <ul className="space-y-1">
                    {settings.privateBlocks
                      .filter(block => {
                        if (!block.date) return false;
                        try {
                          const blockDate = new Date(block.date);
                          if (isNaN(blockDate.getTime())) return false;
                          return blockDate.toISOString().split('T')[0] === selectedDate;
                        } catch (error) {
                          console.error("Invalid date in privateBlocks:", block.date);
                          return false;
                        }
                      })
                      .map((block, index) => (
                        <li key={index}>
                          {block.startHour}:00 - {block.endHour}:00
                          {block.description && ` (${block.description})`}
                        </li>
                      ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calendar;
